import React, { useMemo, useState } from 'react';
import { GATEWAY, SEDI, SOCIETA, Colonnina, formatDataOra } from './dati';
import { Kpi, Prese, StatoColonnina } from './componenti';

interface Props {
  colonnine: Colonnina[];
  gatewayOk: boolean;
  onCrea: (colonnina: Colonnina) => void;
  onGatewayOk: (ok: boolean) => void;
  onApri: (id: number) => void;
}

interface FormNuova {
  nome: string;
  codiceOcpp: string;
  sedeId: string;
}

const FORM_VUOTO: FormNuova = { nome: '', codiceOcpp: '', sedeId: '' };

export default function VistaColonnine({ colonnine, gatewayOk, onCrea, onGatewayOk, onApri }: Props) {
  // Il filtro società fa le veci della società scelta in alto nella topbar
  const [fSocieta, setFSocieta] = useState('');
  const [fQ, setFQ] = useState('');
  const [form, setForm] = useState<FormNuova | null>(null);
  const [errore, setErrore] = useState('');

  const societa = SOCIETA.find((s) => s.id === Number(fSocieta)) ?? null;

  const visibili = useMemo(() => {
    const q = fQ.trim().toLowerCase();
    return colonnine.filter((c) => {
      if (fSocieta && c.societaId !== Number(fSocieta)) return false;
      return !q || `${c.nome} ${c.codiceOcpp} ${c.sede?.nome ?? ''}`.toLowerCase().includes(q);
    });
  }, [colonnine, fSocieta, fQ]);

  const prese = visibili.flatMap((c) => c.connettori);
  const collegate = visibili.filter((c) => c.collegata).length;

  const apriNuova = () => {
    setErrore('');
    setForm(FORM_VUOTO);
  };

  const salva = () => {
    if (!form || !societa) return;
    const codice = form.codiceOcpp.trim();
    if (!form.nome.trim()) { setErrore('Il nome è obbligatorio'); return; }
    if (!/^[A-Z0-9_-]{3,40}$/.test(codice)) {
      setErrore('Codice OCPP: da 3 a 40 caratteri fra lettere maiuscole, cifre, trattino e trattino basso');
      return;
    }
    if (colonnine.some((c) => c.codiceOcpp === codice)) {
      setErrore(`Il codice ${codice} è già usato da un'altra colonnina`);
      return;
    }
    const nuova: Colonnina = {
      id: Date.now(), societaId: societa.id, societaNome: societa.nome, nome: form.nome.trim(), codiceOcpp: codice,
      sede: SEDI.find((s) => s.id === Number(form.sedeId)) ?? null,
      marca: null, modello: null, seriale: null, firmware: null, collegata: false, ultimoContattoAt: null,
      attiva: true, passwordImpostata: false, connettori: [],
    };
    onCrea(nuova);
    setForm(null);
  };

  return (
    <div className="bop-ricarica__stack">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Colonnine</h2>
          <p className="bop-page-subtitle">Colonnine di ricarica collegate al gateway OCPP</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={apriNuova} disabled={!societa}
          title={societa ? undefined : 'Scegli una società per creare una colonnina'}>
          + Nuova colonnina
        </button>
      </div>

      {gatewayOk ? (
        <div className="bop-alert bop-alert--success bop-mb-0 bop-flex-between">
          <span>Gateway collegato · ultimo contatto {formatDataOra(GATEWAY.ultimoContattoAt)}</span>
          <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => onGatewayOk(false)}>
            Simula gateway non raggiungibile
          </button>
        </div>
      ) : (
        <div className="bop-alert bop-alert--danger bop-mb-0 bop-flex-between">
          <span>
            Gateway non raggiungibile dalle {formatDataOra(GATEWAY.ultimoContattoAt)}: lo stato delle colonnine
            non è aggiornato. Le ricariche continuano con i badge salvati sulle colonnine.
          </span>
          <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => onGatewayOk(true)}>Ripristina</button>
        </div>
      )}

      <div className="bop-ricarica__kpi-grid">
        <Kpi label="Colonnine collegate" valore={gatewayOk ? `${collegate} su ${visibili.length}` : '—'} />
        <Kpi label="Prese in carica"
          valore={gatewayOk ? String(prese.filter((p) => p.stato === 'Charging').length) : '—'} />
        <Kpi label="Prese guaste"
          valore={gatewayOk ? String(prese.filter((p) => p.stato === 'Faulted').length) : '—'} />
      </div>

      <div className="bop-filter-bar">
        <select className="bop-select" value={fSocieta} onChange={(e) => setFSocieta(e.target.value)}>
          <option value="">Tutte le società</option>
          {SOCIETA.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
        </select>
        <input className="bop-input" placeholder="Cerca nome, codice o sede…" value={fQ}
          onChange={(e) => setFQ(e.target.value)} />
      </div>

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {visibili.length === 0 ? (
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Nessuna colonnina</h3>
              <p className="bop-empty-state__text">Nessuna colonnina trovata con i filtri selezionati</p>
            </div>
          ) : (
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Colonnina</th>
                  <th>Società</th>
                  <th>Sede</th>
                  <th>Stato</th>
                  <th>Prese</th>
                  <th>Ultimo contatto</th>
                  <th>Modello</th>
                </tr>
              </thead>
              <tbody>
                {visibili.map((c) => (
                  <tr key={c.id} onClick={() => onApri(c.id)}>
                    <td>
                      <strong>{c.nome}</strong>
                      <div className="bop-ricarica__secondaria bop-text-mono">{c.codiceOcpp}</div>
                    </td>
                    <td>{c.societaNome}</td>
                    <td className="bop-text-muted">{c.sede?.nome ?? '—'}</td>
                    <td><StatoColonnina colonnina={c} gatewayOk={gatewayOk} /></td>
                    <td><Prese connettori={c.connettori} gatewayOk={gatewayOk} /></td>
                    <td className="bop-text-muted">{formatDataOra(c.ultimoContattoAt)}</td>
                    <td className="bop-text-muted">{c.marca ? `${c.marca} ${c.modello ?? ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {form && societa && (
        <div className="bop-modal-overlay" onClick={() => setForm(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Nuova colonnina</h2>
              <button className="bop-modal__close" onClick={() => setForm(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              {errore && <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>}
              <p className="bop-ricarica__nota">La colonnina viene creata in {societa.nome}.</p>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="col-nome">
                  Nome <span className="bop-field__required">*</span>
                </label>
                <input id="col-nome" className="bop-field__input" placeholder="Es. Parcheggio ingresso 3"
                  value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </div>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="col-codice">
                  Codice OCPP <span className="bop-field__required">*</span>
                </label>
                <input id="col-codice" className="bop-field__input bop-text-mono" placeholder="Es. PIA-SEDE-03"
                  value={form.codiceOcpp}
                  onChange={(e) => setForm({ ...form, codiceOcpp: e.target.value.toUpperCase() })} />
                <p className="bop-field__hint">
                  È l'identificativo che l'installatore configura sulla colonnina: si collegherà a
                  wss://ocpp.pianinvest.it/{form.codiceOcpp || 'CODICE'}
                </p>
              </div>
              <div className="bop-field">
                <label className="bop-field__label" htmlFor="col-sede">Sede</label>
                <select id="col-sede" className="bop-field__select" value={form.sedeId}
                  onChange={(e) => setForm({ ...form, sedeId: e.target.value })}>
                  <option value="">Nessuna sede</option>
                  {SEDI.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
                </select>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setForm(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva}>Crea colonnina</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
